import { AppDate } from '@macs-frontend/core/utils/date';
import { AppDateTime } from '@macs-frontend/core/utils/datetime';
import { UUID } from '@macs-frontend/core/utils/uuid';
import {
  Client,
  CompanyCategory,
  ProjectTeamMember,
  Role,
} from '@macs-frontend/web-macs/shared';
import { BaseEntity } from './base-entity.model';
import { BaseClient } from './client';
import { BaseCompany } from './company.model';
import { UserRoleUnit } from './role.model';
import { BaseTeamMember } from './team-member.model';
import { Timezone } from './timezone';
import { TaskRole } from '@macs-gql';
import { ProjectTaskAssignment } from '@macs-gql';
import { Boxed } from 'ngrx-forms';
import { OccupationType } from '@macs-frontend/features/company-management/agenda/agenda.types';

export interface Phone {
  label: string;
  number: string;
}

export interface Email {
  label: string;
  email: string;
}

export enum UserStatuses {
  ALL = 'ALL',
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  CANCELED_INVITATION = 'CANCELED_INVITATION',
}

export interface BaseUser {
  uuid: UUID;
  firstName: string;
  username: string;
  lastName: string;
  fullName?: string;
  birthDate: string;
  status: UserStatuses;
  password: string;
  contactPhotoFilename: string;
  title: string;
  language: string;
  capacity: number;
  currentBillableRate: number;
  hasActiveTimeTracks: boolean;
  costRate: number;
  createdAt?: AppDateTime;
  tankCode?: string;
  socialSecurityNumber: string;
  phone: string;
  clientUser: boolean;
  companyUser: boolean;
}

export interface UserBillableRate extends Omit<BaseEntity, 'uuid'> {
  uuid: null | UUID;
  applyFrom?: AppDate;
  rate: number;
}

export interface ClientUser extends BaseEntity {
  client: BaseClient;
  roles: Role[];
  userUuid: UUID;
}

export interface User extends BaseUser {
  companies: BaseCompany[];
  projectTeamMembers: ProjectTeamMember[];
  projectTaskAssignments: ProjectTaskAssignment[];
  phones: Phone[];
  emails: Email[];
  timezone: Timezone;
  billableRates: UserBillableRate[];
  roleUnits?: UserRoleUnit[];
  currentRoleUnits?: UserRoleUnit[];
  taskRoles?: TaskRole[];
  occupationTypes?: OccupationType[];
  clientUsers?: ClientUser[];
}

export interface UserFormBillableRate {
  applyFrom: null | AppDate;
  rate?: string;
  applyPeriod: UserBillableRateApplyPeriods;
}

export interface UserForm {
  username: string;
  firstName: string;
  lastName: string;
  phones: Phone[];
  phone: string;
  birthDate: string;
  title: string;
  language: string;
  timezone: string;
  emails: Email[];
  capacity: number;
  billableRate: UserFormBillableRate;
  costRate: number;
  socialSecurityNumber: string;
  taskRoles: Boxed<TaskRole[]>;
  occupationTypes: Boxed<OccupationType[]>;
}

export interface UserProjectClient extends BaseClient {
  readonly category: CompanyCategory;
}

export interface BaseUserProject extends BaseEntity {
  number: string;
  logoFilename: string;
  name: string;
  status: string;
  logo?: string;
}

export interface UserProject extends BaseUserProject {
  client: UserProjectClient;
  teamMembers?: BaseTeamMember[];
}

export enum UserBillableRateApplyPeriods {
  APPLY_TO_ALL_TIME = 'APPLY_TO_ALL_TIME',
  APPLY_FROM_CERTAIN_TIME = 'APPLY_FROM_CERTAIN_TIME',
}
